import './CaseResolutionList.scss';

import { List, useTable } from '@refinedev/antd';
import { Flex } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppTable from '@/components/general/AppTable';
import LinkTextField from '@/components/general/LinkTextField';
import TicketLogisticStateTag from '@/components/ticket/TicketLogisticStateTag';
import TicketStateTag from '@/components/ticket/TicketStateTag';
import { COMPANY_URLS, MACHINES_URLS } from '@/constants/urls';
import { useUserContext } from '@/contexts/UserContext';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import { Ticket } from '@/model/Ticket/Ticket';
import { RESOURCES } from '@/Resources';

import CaseResolutionFilter from './CaseResolutionFilter/CaseResolutionFilter';

const CaseResolutionList = () => {
  const { t } = useTranslation();
  const { pageSizePreference } = useUserContext();
  const [isReady, setIsReady] = useState(false);
  const {
    tableProps,
    filters,
    setFilters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    sorters,
    setSorters,
  } = useTable<Ticket>({
    resource: RESOURCES.TICKETS,
    meta: { has_operations_associated: true, include_logistics_vars: true },
    queryOptions: {
      refetchInterval: 300000, // 5 minutes
      enabled: isReady,
    },
    pagination: {
      pageSize: pageSizePreference,
    },
  });

  useListStatePersistence({
    resource: RESOURCES.CASE_RESOLUTION,
    filters,
    setFilters,
    sorters,
    setSorters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    onReady: () => setIsReady(true),
  });

  return (
    <List
      headerButtons={() => (
        <CaseResolutionFilter filters={filters} setFilters={setFilters} setCurrent={setCurrent} />
      )}>
      <AppTable
        {...tableProps}
        pagination={{
          ...tableProps.pagination,
          pageSizeOptions: [20, 40, 60, 80], // Opciones de cantidad por página
          showSizeChanger: true, // Muestra el selector
          showTotal: (total, range) => {
            return `${range[0]} - ${range[1]} ${t('de')} ${total}`;
          },
        }}
        rowKey={'id'}
        scroll={{ x: 800, y: 'calc(100dvh - 310px)' }}>
        <AppTable.Column
          title={t('Caso')}
          width={240}
          render={(_, record) => (
            <LinkTextField
              label={`${record.id} - ${record.ticket_title_str}`}
              to={`/case_resolution/${record.id}`}
              local
              withoutColor
            />
          )}
        />
        <AppTable.Column
          title={t('Empresa')}
          width={200}
          render={(_, record) => (
            <LinkTextField
              admin
              to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record?.empresa}`}
              label={record?.empresa_str || ''}
            />
          )}
        />

        <AppTable.Column
          title={t('Máquina')}
          width={200}
          render={(_, record) =>
            record?.movil ? (
              <LinkTextField
                local
                to={`${MACHINES_URLS.ADMIN_MOVIL}/${record?.movil}`}
                label={`${record?.movil_str ?? ''} (${record?.movil})`}
              />
            ) : (
              '-'
            )
          }
        />
        <AppTable.Column
          title={t('Cantidad de Envíos')}
          width={120}
          render={(_, record) => record?.shipment_ids.length ?? 0}
        />
        <AppTable.Column
          title={t('Cantidad de Recepciones')}
          width={120}
          render={(_, record) => record?.reception_ids.length ?? 0}
        />
        <AppTable.Column
          title={t('Elementos facturados')}
          width={120}
          render={(_, record) => record?.logistics_billed_items ?? 0}
        />
        <AppTable.Column
          title={t('Elementos bonificados')}
          width={120}
          render={(_, record) => record?.logistics_bonus_items ?? 0}
        />
        <AppTable.Column
          title={t('Elementos en garantía')}
          width={120}
          render={(_, record) => record?.logistics_warranty_items ?? 0}
        />
        <AppTable.Column
          title={t('Estado del ticket')}
          width={120}
          render={(_, record) => (
            <Flex align="center" justify="center">
              {record?.logistics_state != undefined ? (
                <TicketStateTag ticketState={record.status} />
              ) : (
                ''
              )}
            </Flex>
          )}
        />
        <AppTable.Column
          fixed="right"
          title={t('Estado')}
          width={120}
          render={(_, record) => (
            <Flex align="center" justify="center">
              {record?.logistics_state != undefined ? (
                <TicketLogisticStateTag ticketLogisticState={record.logistics_state} />
              ) : (
                ''
              )}
            </Flex>
          )}
        />
      </AppTable>
    </List>
  );
};

export default CaseResolutionList;
